import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  catchError,
  formatDatetime,
  isComponentDisabled,
} from "@_utils/helpers";
import styles from "./LicensePopup.module.css";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import FileIcon from "@mui/icons-material/FileCopy";
import { LicenseConstants } from "@_constants/license.constants";
import { caseActivityActions } from "@_actions/caseActivity.actions";

const componetTag = "license_popup";
function LicensePopup(props) {
  const dispatch = useDispatch();
  const { data, caseId, resumeAutomation, filterLicenseId, close } = props;
  const inputDisabled = isComponentDisabled(componetTag, data.state);
  const caseReducer = useSelector((state) => state.caseReducer);

  const [findings, setFindings] = useState([]);
  const [licencePopupSubmitInProgress, setLicencePopupSubmitInProgress] =
    useState(false);

  useEffect(() => {
    let source = data.findings ? JSON.parse(JSON.stringify(data.findings)) : [];
    if (filterLicenseId) {
      source = source.filter(
        (f) => f.license && f.license.id === filterLicenseId,
      );
    }
    setFindings(source);
  }, [data, filterLicenseId]);

  const handleChange = (e, index) => {
    const { value } = e.target;
    let newFindings = [...findings];
    newFindings[index].license.state = value;
    setFindings(newFindings);
  };

  function actionClicked() {
    findings &&
      findings.map((finding, index) => {
        if (finding.license) {
          setLicencePopupSubmitInProgress(true);
          dispatch(
            caseActions.updateCaseLicenseState(caseId, {
              state: finding.license.state,
              id: finding.id,
            }),
          );
          return true;
        } else {
          return false;
        }
      });
  }

  /* Extract updateLicenseStatus & deleteLicenseStatus into stable variables to fix CI Error */
  const updateLicenseStatus = caseReducer.status["CASE_UPDATE_LICENSE_STATE"];
  const deleteLicenseStatus = caseReducer.status["CASE_DELETE_LICENSE"];

  useEffect(() => {
    if (
      (licencePopupSubmitInProgress && updateLicenseStatus === "success") ||
      deleteLicenseStatus === "success"
    ) {
      setLicencePopupSubmitInProgress(false);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_LICENSE_STATE"));
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      //update license was successfull, reload cases, the case and activities
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      dispatch(caseActions.getCaseWithChargeDetails(caseId, data.overview));
      dispatch(caseActivityActions.getCaseActivities(caseId));
      if (
        findings.some(
          (finding) =>
            finding.license?.state === LicenseConstants.STATE_INVALID,
        )
      ) {
        resumeAutomation && resumeAutomation();
      }
      close();
    } else if (
      (licencePopupSubmitInProgress && catchError(updateLicenseStatus)) ||
      catchError(deleteLicenseStatus)
    ) {
      setLicencePopupSubmitInProgress(false);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_LICENSE_STATE"));
      dispatch(caseActions.clearStateMessage("CASE_DELETE_LICENSE"));
      if (catchError(updateLicenseStatus))
        window.alert("Update license(s) failed.");
    }
    /* Only depend on the reducer status + in-progress flag so the effect runs
    once per submit cycle (other deps change every render and would loop). */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLicenseStatus, deleteLicenseStatus, licencePopupSubmitInProgress]);

  function cancelAction() {
    props.close();
    return;
  }

  // Safety net: if findings filtered down to nothing (e.g. license was deleted/replaced and the case data is stale),
  // show a placeholder instead of an empty popup.
  const hasFindings = findings && findings.some((f) => f && f.license);

  return (
    <DialogPopup
      title="Uploaded Licence"
      open={props.open}
      onClose={() => {
        props.close();
      }}
    >
      <div className={styles.content_container}>
        <div className={styles.findings_container}>
          {hasFindings ? (
            findings.map((finding, index) => {
              if (finding.license)
                return (
                  <div className={styles.finding_container} key={index}>
                    <a
                      className={styles.license_link}
                      href={finding.license.license_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className={styles.finging_image_container}>
                        <FileIcon />
                      </div>
                    </a>
                    <div className={styles.finging_details_container}>
                      <a
                        className={styles.finding_link}
                        href={finding.license.license_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {finding.license.filename}
                      </a>
                      <div className={styles.finding_date}>
                        {formatDatetime(
                          finding.license.date_uploaded,
                          "medium",
                        )}
                      </div>
                      {finding.license?.note && (
                        <div className={styles.finding_note}>
                          <b>NOTE: </b> {finding.license.note}
                        </div>
                      )}
                    </div>
                    <div className={styles.finging_validity_container}>
                      <RadioGroup
                        name="licenseValidity"
                        value={finding.license.state}
                        onChange={(e) => handleChange(e, index)}
                      >
                        <FormControlLabel
                          value="VALID"
                          control={<Radio />}
                          label="Valid"
                          disabled={inputDisabled}
                        />
                        <FormControlLabel
                          value="INVALID"
                          control={<Radio />}
                          label="Invalid"
                          disabled={inputDisabled}
                        />
                      </RadioGroup>
                    </div>
                  </div>
                );
              else return null;
            })
          ) : (
            <div className={styles.empty_state}>
              This license is no longer available. Please refresh the page.
            </div>
          )}
        </div>
      </div>
      <div className="action_container">
        <>
          <Button className="btn_cancel" onClick={cancelAction}>
            Cancel
          </Button>
          <Button
            className="btn_confirm"
            onClick={actionClicked}
            disabled={!hasFindings}
          >
            Confirm
          </Button>
        </>
      </div>
    </DialogPopup>
  );
}

export default LicensePopup;
